import { BASE_URL, COMMANDBAR_CUSTOMERID } from '@synergy/config';
import { FeatureFlag, UserRole } from '@synergy/models';
import { LocalizedProvider, PoolSpinner } from '@synergy/components';
import { Notifier } from '@synergy/shared/snackbar/feature';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCommandbar } from '@synergy/components-next';
import { useEffect } from 'react';

import { AccessControlConnect } from './common/components/access-control/access-control-connect';
import { AccessControlConnectRole } from './common/components/access-control/access-control-connect-role';
import { ContactsLoginsReviewPopup } from './features/contacts-logins/components/review-popup';
import { PaloAppLayout } from './palo-app-layout';
import { PaloBonusSettlementDetail } from './features/bonus-settlements/details/palo/palo-bonus-settlement-detail';
import { PaloBonusSettlementOverview } from './features/bonus-settlements/overview/palo/palo-bonus-settlement-overview';
import { PaloBonusSettlementPoolDetail } from './features/bonus-settlements/details/pool-details/palo/palo-bonus-settlement-pool-detail';
import { PaloBuyAgain } from './features/buy-again/palo/palo-buy-again';
import { PaloContactsLoginsOverview } from './features/contacts-logins/palo/palo-contacts-logins-overview';
import { PaloDemandsDetail } from './features/demands/demands-detail/palo/palo-demands-detail';
import { PaloDemandsOverview } from './features/demands/demands-overview/palo/palo-demands-overview';
import { PaloDemandsTopics } from './features/demands/demands-topics/palo/palo-demands-topics';
import { PaloInquiryOverview } from './features/inquiry/palo/palo-inquiry-overview';
import { PaloNotifications } from './features/notifications/palo/palo-notifications';
import { PaloOrderApprovals } from './features/order-approvals/palo/palo-order-approvals';
import { PaloOrdersOverview } from './features/orders/palo/palo-orders-overview';
import { ROUTES } from './features/sidebar-menu/navigation-drawer/routes';
import { fetchPositions } from './common/actions/positions-actions';
import { isRequesting } from './common/selectors/requesting-selector';
import { localesSelector } from './common/selectors/locale-selectors';
import { useSelector } from './store/use-appstate-selector';
import { useThunkDispatch } from './store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        backgroundColor: theme.palette.background.default,
        height: '100%',
        display: 'flex',
        overflow: 'hidden',
        flex: 1,
        flexDirection: 'column',
    },
    root: {
        flexGrow: 1,
        height: '100%',
    },
    content: {
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flex: 1,
    },
}));

const ContactsLoginsRestricted = AccessControlConnectRole(UserRole.ShopAdmin, true)(PaloContactsLoginsOverview);
const NotificationsRestricted = AccessControlConnectRole(UserRole.ShopAdmin, true)(PaloNotifications);
const BonusSettlementOverviewRestricted = AccessControlConnect(
    [UserRole.ShopAdmin, UserRole.CustomerBonusContact],
    FeatureFlag.BonusSettlements,
    true
)(PaloBonusSettlementOverview);

const BonusSettlementDetailRestricted = AccessControlConnect(
    [UserRole.ShopAdmin, UserRole.CustomerBonusContact],
    FeatureFlag.BonusSettlements,
    true
)(PaloBonusSettlementDetail);
const BonusSettlementPoolDetailRestricted = AccessControlConnect(
    [UserRole.ShopAdmin, UserRole.CustomerBonusContact],
    FeatureFlag.BonusSettlements,
    true
)(PaloBonusSettlementPoolDetail);

export const App = () => {
    const enablePoolSpinner = useSelector(isRequesting);
    const locales = useSelector(localesSelector);
    const userId = useSelector(state => state.user?.id);
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    useCommandbar(COMMANDBAR_CUSTOMERID, userId);

    useEffect(() => {
        dispatch(fetchPositions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LocalizedProvider locales={locales}>
            <div className={classes.root}>
                <Router basename={BASE_URL || '/'}>
                    <PaloAppLayout>
                        <ContactsLoginsReviewPopup />
                        <div className={classes.content}>
                            <PoolSpinner enabled={enablePoolSpinner} className={classes.container}>
                                <Routes>
                                    <Route path={ROUTES.demands.overview} element={<PaloDemandsOverview />} />
                                    <Route path={ROUTES.demands.detail} element={<PaloDemandsDetail />} />
                                    <Route path={ROUTES.topics} element={<PaloDemandsTopics />} />
                                    <Route path={ROUTES.userContacts} element={<ContactsLoginsRestricted />} />
                                    <Route
                                        path={ROUTES.bonusSettlements.overview}
                                        element={<BonusSettlementOverviewRestricted />}
                                    />
                                    <Route
                                        path={ROUTES.bonusSettlements.detail.base + '/*'}
                                        element={<BonusSettlementDetailRestricted />}
                                    />
                                    <Route
                                        path={ROUTES.bonusSettlements.poolDetails + '/*'}
                                        element={<BonusSettlementPoolDetailRestricted />}
                                    />
                                    <Route path={ROUTES.inquiries} element={<PaloInquiryOverview />} />
                                    <Route path={ROUTES.orders} element={<PaloOrdersOverview />} />
                                    <Route path={ROUTES.orderApprovals} element={<PaloOrderApprovals />} />
                                    <Route path={ROUTES.buyAgain} element={<PaloBuyAgain />} />
                                    <Route path={ROUTES.notifications} element={<NotificationsRestricted />} />
                                    <Route path="*" element={<Navigate to={ROUTES.demands.overview} />} />
                                </Routes>
                            </PoolSpinner>
                        </div>
                    </PaloAppLayout>
                </Router>
                <Notifier />
            </div>
        </LocalizedProvider>
    );
};
